import { useFetcher } from '@remix-run/react';
import { useCallback, useEffect } from 'react';
import type { FetcherData } from 'types';

import ErrorIcon from '~/assets/icons/Error';
import TextInput from '~/components/Form/TextInput';
import useUser from '~/hooks/useUser';
import braze from '~/services/braze.client';
import gtm, { LoginSource } from '~/services/gtm.client';
import { isInAppBrowser } from '~/utils/browser';

import Tracked from '../../Tracked';
import type { ScreenProps } from '.';
import AppleLogin from './ExternalLoginComponents/AppleLogin';
import FacebookLoginButton from './ExternalLoginComponents/Facebook/FacebookLoginButton';
import GoogleLoginButton from './ExternalLoginComponents/GoogleLoginButton';
import ModalHeadings from './ModalHeadings';
import OrSeparator from './OrSeperator';

const AuthenticationErrorMessage = ({ isActive }: { isActive: boolean }) => {
  return isActive ? (
    <div className="flex flex-row items-center justify-start gap-2">
      <div className="shrink-0">
        <ErrorIcon />
      </div>
      <p className="text-sm font-semibold text-white">
        Incorrect password and/or email used. Please try again. You may have
        previously logged in with a Facebook account.
      </p>
    </div>
  ) : (
    <></>
  );
};

const SignInScreen = ({
  onDismiss,
  setScreen,
  postAuthOptions,
  setError
}: ScreenProps) => {
  const fetcher = useFetcher<FetcherData>();
  const user = useUser();

  const errors = fetcher.data?.errors;
  const idle = fetcher.state === 'idle';

  const onSignIn = useCallback(
    (loginType: LoginSource) => () => {
      gtm.sendLoginEvent(user.id, loginType);

      braze.setUser(user.id);
      braze.logLoginEvent();

      onDismiss({ loginSuccessful: true });
    },
    [user.id, onDismiss]
  );

  useEffect(() => {
    if (fetcher.data?.success === true) onSignIn(LoginSource.Email)();
  }, [fetcher.data?.success, onSignIn]);

  const isInApp = isInAppBrowser(navigator.userAgent);

  return (
    <>
      <ModalHeadings>Sign in with your email and password.</ModalHeadings>
      <fetcher.Form
        action={`/bolt/login/email${postAuthOptions?.sizes ? '?sizes=' + JSON.stringify(postAuthOptions.sizes) : ''}`}
        className="flex w-full flex-col items-start gap-4 bg-black"
        method="post"
      >
        <input
          type="hidden"
          value={postAuthOptions?.legacyProductId}
          name="legacyProductId"
        />
        <input
          type="hidden"
          value={postAuthOptions?.redirectUrl}
          name="redirectUrl"
        />
        <input type="hidden" value={user.guestId} name="guestId" />
        <input type="hidden" value={postAuthOptions?.action} name="action" />
        <TextInput
          className="h-12 w-full rounded-md p-4"
          error={
            errors?.email ? 'This is an incorrect email format' : undefined
          }
          id="login-email"
          name="email"
          placeholder="Email address"
          type="email"
        />
        <TextInput
          className="h-12 w-full rounded-md p-4"
          error={
            errors?.password
              ? 'Password must be at least 8 characters'
              : undefined
          }
          id="login-email"
          name="password"
          placeholder="Password"
          type="password"
        />
        <button
          onClick={() => setScreen('forgotPassword')}
          type="button"
          className="text-green-human"
        >
          Forgot Password
        </button>
        <Tracked.Button
          className="h-12"
          variant="green-human"
          disabled={!idle}
          name="SIGN_IN_EMAIL"
          pagePosition={'Sign in modal'}
        >
          {idle ? 'Sign in' : 'Signing in...'}
        </Tracked.Button>
        <AuthenticationErrorMessage
          isActive={Boolean(fetcher?.data?.errors?.authentication)}
        />
      </fetcher.Form>

      {!isInApp && (
        <>
          <OrSeparator />
          <div className="flex gap-4">
            <GoogleLoginButton
              postAuthOptions={postAuthOptions}
              onSuccess={onSignIn(LoginSource.Google)}
              setError={setError}
            />
            <FacebookLoginButton
              postAuthOptions={postAuthOptions}
              onSuccess={onSignIn(LoginSource.Meta)}
              setError={setError}
            />
            <AppleLogin.Button
              postAuthOptions={postAuthOptions}
              onSuccess={onSignIn(LoginSource.Apple)}
              setError={setError}
            />
          </div>
        </>
      )}
    </>
  );
};

export default SignInScreen;
